import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, CircularProgress, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Divider, IconButton, useTheme, Container, InputAdornment, Tooltip, MenuItem
} from '@mui/material';
import {
  Add as AddIcon, Refresh as RefreshIcon, Assignment as AssignmentIcon,
  Search as SearchIcon, ArrowBack as ArrowBackIcon,
  DarkMode as DarkModeIcon, LightMode as LightModeIcon,
  Description as DescriptionIcon, Category as CategoryIcon,
  PriorityHigh as HighPriorityIcon, RemoveCircle as MediumPriorityIcon,
  LowPriority as LowPriorityIcon
} from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import { ticketsService, type Ticket } from '../services/ticketsService';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../contexts/ThemeModeContext';

const MyTicketsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { mode, toggleMode } = useThemeMode();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [categories] = useState<string[]>([
    'Access Request',
    'Badge Request', 
    'Technical Issue',
    'Account Setup',
    'General Inquiry'
  ]);
  const [newTicket, setNewTicket] = useState({
    summary: '',
    description: '',
    priority: 'medium' as 'critical' | 'high' | 'medium' | 'low',
    category: '',
  });

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const data = await ticketsService.listMyTickets();
      setTickets(data);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const handleCreateTicket = async () => {
    if (!newTicket.summary.trim()) return;

    try {
      await ticketsService.createTicket({
        issue_description: newTicket.description || newTicket.summary,
        summary: newTicket.summary,
        description: newTicket.description,
        priority: newTicket.priority,
        metadata: {
          ...(newTicket.category ? { category: newTicket.category } : {}),
        },
      });
      setCreateOpen(false);
      setNewTicket({ summary: '', description: '', priority: 'medium', category: '' });
      await fetchTickets();
    } catch (error) {
      console.error('Failed to create ticket:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return theme.palette.info.main;
      case 'in_progress': return theme.palette.warning.main;
      case 'resolved': return theme.palette.success.main;
      case 'closed': return theme.palette.grey[500];
      default: return theme.palette.grey[500];
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return '#dc2626';
      case 'high': return '#f59e0b';
      case 'medium': return '#3b82f6';
      case 'low': return '#10b981';
      default: return theme.palette.grey[500];
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    // Search filter
    const matchesSearch = !search || 
      ticket.summary?.toLowerCase().includes(search.toLowerCase()) ||
      ticket.issue_description.toLowerCase().includes(search.toLowerCase()) ||
      ticket.ticket_id.toLowerCase().includes(search.toLowerCase());
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    
    // Priority filter
    const matchesPriority = priorityFilter === 'all' || ticket.priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: theme.palette.background.default,
    }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/chat')} size="small">
              <ArrowBackIcon />
            </IconButton>
            <img 
              src="/atlasai-icon.svg" 
              alt="CoreSupport" 
              style={{ width: 32, height: 32 }}
            />
            <Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                fontSize: '1.1rem',
                lineHeight: 1.2
              }}>
                CoreSupport
              </Typography>
              <Typography variant="caption" sx={{ 
                color: 'text.secondary',
                fontSize: '0.75rem'
              }}>
                My Support Tickets
              </Typography>
            </Box>
          </Box>
          <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            <IconButton onClick={toggleMode} color="primary">
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary">
            View and manage your support requests
          </Typography>
        </Box>

      {/* Actions Bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search tickets..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, minWidth: 250 }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          SelectProps={{ native: true }}
          sx={{ minWidth: 130 }}
        >
          <option value="all">All Status</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </TextField>
        <TextField
          select
          size="small"
          label="Priority"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          SelectProps={{ native: true }}
          sx={{ minWidth: 130 }}
        >
          <option value="all">All Priority</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </TextField>
        <IconButton onClick={fetchTickets} color="primary">
          <RefreshIcon />
        </IconButton>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ borderRadius: 1 }}
        >
          New Ticket
        </Button>
      </Box>

      {/* Tickets List */}
      {filteredTickets.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {search ? 'No tickets found' : 'No tickets yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {search ? 'Try a different search term' : 'Create your first support ticket to get started'}
          </Typography>
          {!search && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
            >
              Create Ticket
            </Button>
          )}
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {filteredTickets.map((ticket) => (
            <Card
              key={ticket.ticket_id}
              onClick={() => navigate(`/ticket/${ticket.ticket_id}`)}
              sx={{
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 3,
                background: mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(15, 15, 15, 0.95) 0%, rgba(25, 25, 25, 0.95) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
                border: mode === 'dark'
                  ? '1px solid rgba(71, 85, 105, 0.3)'
                  : '1px solid rgba(226, 232, 240, 0.8)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: mode === 'dark'
                    ? '0 20px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(139, 92, 246, 0.3)'
                    : '0 20px 40px rgba(99, 102, 241, 0.15), 0 0 0 1px rgba(99, 102, 241, 0.2)',
                  borderColor: mode === 'dark' ? 'rgba(139, 92, 246, 0.5)' : 'rgba(99, 102, 241, 0.3)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: mode === 'dark'
                    ? 'linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%)'
                    : 'linear-gradient(90deg, #6366f1 0%, #4f46e5 100%)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1, pr: 2 }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700, 
                      mb: 1,
                      color: mode === 'dark' ? '#f8fafc' : '#0f172a',
                      fontSize: '1.125rem',
                    }}>
                      {ticket.summary || ticket.issue_description}
                    </Typography>
                    {ticket.summary && ticket.issue_description !== ticket.summary && (
                      <Typography variant="body2" sx={{ 
                        color: mode === 'dark' ? 'rgba(248, 250, 252, 0.7)' : 'rgba(15, 23, 42, 0.7)',
                        mb: 2,
                        lineHeight: 1.6,
                      }}>
                        {ticket.issue_description}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                    <Chip
                      label={ticket.status.replace('_', ' ').toUpperCase()}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(ticket.status),
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 24,
                        borderRadius: 2,
                        letterSpacing: '0.5px',
                      }}
                    />
                    <Chip
                      label={ticket.priority.toUpperCase()}
                      size="small"
                      sx={{
                        backgroundColor: getPriorityColor(ticket.priority),
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 24,
                        borderRadius: 2,
                        letterSpacing: '0.5px',
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 3, 
                  pt: 2,
                  borderTop: mode === 'dark' 
                    ? '1px solid rgba(71, 85, 105, 0.2)' 
                    : '1px solid rgba(226, 232, 240, 0.6)',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: mode === 'dark' ? '#8b5cf6' : '#6366f1',
                    }} />
                    <Typography variant="caption" sx={{ 
                      color: mode === 'dark' ? 'rgba(248, 250, 252, 0.6)' : 'rgba(15, 23, 42, 0.6)',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                    }}>
                      {ticket.ticket_id.slice(0, 8)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: mode === 'dark' ? '#8b5cf6' : '#6366f1',
                    }} />
                    <Typography variant="caption" sx={{ 
                      color: mode === 'dark' ? 'rgba(248, 250, 252, 0.6)' : 'rgba(15, 23, 42, 0.6)',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                    }}>
                      {new Date(ticket.created_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                  {ticket.assigned_to && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: mode === 'dark' ? '#8b5cf6' : '#6366f1',
                      }} />
                      <Typography variant="caption" sx={{ 
                        color: mode === 'dark' ? 'rgba(248, 250, 252, 0.6)' : 'rgba(15, 23, 42, 0.6)',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                      }}>
                        {ticket.assigned_to}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Create Ticket Dialog */}
      <Dialog 
        open={createOpen} 
        onClose={() => setCreateOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            background: mode === 'dark'
              ? 'linear-gradient(135deg, rgba(10, 10, 10, 0.98) 0%, rgba(15, 15, 15, 0.98) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
          }
        }}
      >
        <DialogTitle sx={{ p: 5, pb: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{
            position: 'relative',
            background: mode === 'dark'
              ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
              : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
            borderRadius: 4,
            p: 2,
            boxShadow: mode === 'dark'
              ? '0 8px 32px rgba(139, 92, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
              : '0 8px 32px rgba(99, 102, 241, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
          }}>
            <AddIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ 
              fontWeight: 800,
              background: mode === 'dark'
                ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 0.5,
            }}>
              Create New Ticket
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              opacity: 0.8,
            }}>
              Fill in the details below to create a new support ticket
            </Typography>
          </Box>
        </DialogTitle>
        <Divider sx={{ 
          borderColor: mode === 'dark' 
            ? 'rgba(39, 39, 42, 0.6)' 
            : 'rgba(226, 232, 240, 0.8)',
        }} />
        <DialogContent sx={{ p: 5, pt: 4 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 700, 
              color: mode === 'dark' ? '#ffffff' : '#0f172a',
              mb: 3,
              mt: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}>
              <Box sx={{
                background: mode === 'dark'
                  ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                  : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                borderRadius: 3,
                p: 1,
                boxShadow: mode === 'dark'
                  ? '0 4px 16px rgba(139, 92, 246, 0.3)'
                  : '0 4px 16px rgba(99, 102, 241, 0.2)',
              }}>
                <AssignmentIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              Ticket Information
            </Typography>
            
            <TextField 
              fullWidth 
              label="Ticket Summary" 
              placeholder="Brief description of the issue"
              value={newTicket.summary} 
              onChange={e => setNewTicket({ ...newTicket, summary: e.target.value })} 
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AssignmentIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  background: mode === 'dark' 
                    ? 'rgba(39, 39, 42, 0.6) !important' 
                    : 'rgba(255, 255, 255, 0.95) !important',
                  border: mode === 'dark' 
                    ? '1px solid rgba(39, 39, 42, 0.6)' 
                    : '1px solid rgba(226, 232, 240, 0.8)',
                  '&:hover': {
                    borderColor: mode === 'dark' ? '#8b5cf6' : '#6366f1',
                  },
                  '&.Mui-focused': {
                    borderColor: mode === 'dark' ? '#8b5cf6' : '#6366f1',
                    background: mode === 'dark' 
                      ? 'rgba(39, 39, 42, 0.6) !important' 
                      : 'rgba(255, 255, 255, 0.95) !important',
                    boxShadow: mode === 'dark'
                      ? '0 0 0 2px rgba(139, 92, 246, 0.1)'
                      : '0 0 0 2px rgba(99, 102, 241, 0.1)',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 600,
                },
                '& .MuiOutlinedInput-input': {
                  color: mode === 'dark' ? '#ffffff' : '#0f172a',
                  '&:-webkit-autofill': {
                    WebkitBoxShadow: mode === 'dark' 
                      ? '0 0 0 100px rgba(39, 39, 42, 0.6) inset'
                      : '0 0 0 100px rgba(255, 255, 255, 0.95) inset',
                    WebkitTextFillColor: mode === 'dark' ? '#ffffff' : '#0f172a',
                  },
                },
                '& textarea': {
                  color: mode === 'dark' ? '#ffffff' : '#0f172a',
                },
              }}
            />
            <TextField 
              fullWidth 
              label="Description" 
              placeholder="Detailed description of the issue, steps to reproduce, and any additional context"
              multiline 
              minRows={4} 
              value={newTicket.description} 
              onChange={e => setNewTicket({ ...newTicket, description: e.target.value })} 
              margin="normal"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 2 }}>
                    <DescriptionIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  background: mode === 'dark' 
                    ? 'rgba(39, 39, 42, 0.6) !important' 
                    : 'rgba(255, 255, 255, 0.95) !important',
                  border: mode === 'dark' 
                    ? '1px solid rgba(39, 39, 42, 0.6)' 
                    : '1px solid rgba(226, 232, 240, 0.8)',
                  '&:hover': {
                    borderColor: mode === 'dark' ? '#8b5cf6' : '#6366f1',
                  },
                  '&.Mui-focused': {
                    borderColor: mode === 'dark' ? '#8b5cf6' : '#6366f1',
                    background: mode === 'dark' 
                      ? 'rgba(39, 39, 42, 0.6) !important' 
                      : 'rgba(255, 255, 255, 0.95) !important',
                    boxShadow: mode === 'dark'
                      ? '0 0 0 2px rgba(139, 92, 246, 0.1)'
                      : '0 0 0 2px rgba(99, 102, 241, 0.1)',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 600,
                },
                '& .MuiOutlinedInput-input': {
                  color: mode === 'dark' ? '#ffffff' : '#0f172a',
                  '&:-webkit-autofill': {
                    WebkitBoxShadow: mode === 'dark' 
                      ? '0 0 0 100px rgba(39, 39, 42, 0.6) inset'
                      : '0 0 0 100px rgba(255, 255, 255, 0.95) inset',
                    WebkitTextFillColor: mode === 'dark' ? '#ffffff' : '#0f172a',
                  },
                },
                '& textarea': {
                  color: mode === 'dark' ? '#ffffff' : '#0f172a',
                },
              }}
            />
          </Box>
          <Box sx={{ mb: 4, mt: 4 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 700, 
              color: mode === 'dark' ? '#ffffff' : '#0f172a',
              mb: 3,
              mt: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}>
              <Box sx={{
                background: mode === 'dark'
                  ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                  : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                borderRadius: 3,
                p: 1,
                boxShadow: mode === 'dark'
                  ? '0 4px 16px rgba(245, 158, 11, 0.3)'
                  : '0 4px 16px rgba(245, 158, 11, 0.2)',
              }}>
                <HighPriorityIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              Priority & Category
            </Typography>
            
            <Box display="flex" gap={3} sx={{ mb: 2 }}>
              <TextField 
                fullWidth 
                select 
                label="Priority Level" 
                value={newTicket.priority} 
                onChange={e => setNewTicket({ ...newTicket, priority: e.target.value as any })}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    background: mode === 'dark' 
                      ? 'rgba(39, 39, 42, 0.6) !important' 
                      : 'rgba(255, 255, 255, 0.95) !important',
                    border: mode === 'dark' 
                      ? '1px solid rgba(39, 39, 42, 0.6)' 
                      : '1px solid rgba(226, 232, 240, 0.8)',
                    '&:hover': {
                      borderColor: mode === 'dark' ? '#f59e0b' : '#f59e0b',
                    },
                    '&.Mui-focused': {
                      borderColor: mode === 'dark' ? '#f59e0b' : '#f59e0b',
                      background: mode === 'dark' 
                        ? 'rgba(39, 39, 42, 0.6) !important' 
                        : 'rgba(255, 255, 255, 0.95) !important',
                      boxShadow: mode === 'dark'
                        ? '0 0 0 2px rgba(245, 158, 11, 0.1)'
                        : '0 0 0 2px rgba(245, 158, 11, 0.1)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 600,
                  },
                  '& .MuiSelect-select': {
                    color: mode === 'dark' ? '#ffffff' : '#0f172a',
                  },
                }}
              >
              <MenuItem value="critical">
                <Box display="flex" alignItems="center" gap={1.5}>
                  <HighPriorityIcon fontSize="small" sx={{ color: '#dc2626' }} />
                  Critical
                </Box>
              </MenuItem>
              <MenuItem value="high">
                <Box display="flex" alignItems="center" gap={1.5}>
                  <HighPriorityIcon fontSize="small" sx={{ color: '#f59e0b' }} />
                  High
                </Box>
              </MenuItem>
              <MenuItem value="medium">
                <Box display="flex" alignItems="center" gap={1.5}>
                  <MediumPriorityIcon fontSize="small" sx={{ color: '#3b82f6' }} />
                  Medium
                </Box>
              </MenuItem>
              <MenuItem value="low">
                <Box display="flex" alignItems="center" gap={1.5}>
                  <LowPriorityIcon fontSize="small" sx={{ color: '#10b981' }} />
                  Low
                </Box>
              </MenuItem>
            </TextField>
              <Autocomplete
                fullWidth
                options={categories}
                value={newTicket.category}
                onChange={(_, value) => setNewTicket({ ...newTicket, category: value || '' })}
                freeSolo
                disablePortal
                slotProps={{
                  paper: {
                    sx: {
                      borderRadius: 1,
                      mt: 0.5,
                      boxShadow: mode === 'dark'
                        ? '0 4px 20px rgba(0, 0, 0, 0.5)'
                        : '0 4px 20px rgba(0, 0, 0, 0.1)',
                      border: mode === 'dark'
                        ? '1px solid rgba(39, 39, 42, 0.6)'
                        : '1px solid rgba(226, 232, 240, 0.8)',
                      '& .MuiAutocomplete-listbox': {
                        maxHeight: '240px',
                      },
                    }
                  },
                  popper: {
                    placement: 'bottom-start',
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 4],
                        },
                      },
                    ],
                    sx: {
                      '& .MuiPaper-root': {
                        transformOrigin: 'top',
                        animation: 'fadeIn 0.1s ease-out',
                      },
                      '@keyframes fadeIn': {
                        '0%': {
                          opacity: 0,
                        },
                        '100%': {
                          opacity: 1,
                        },
                      },
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    placeholder="Select or type category"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <CategoryIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    background: mode === 'dark' 
                      ? 'rgba(39, 39, 42, 0.6) !important' 
                      : 'rgba(255, 255, 255, 0.95) !important',
                    border: mode === 'dark' 
                      ? '1px solid rgba(39, 39, 42, 0.6)' 
                      : '1px solid rgba(226, 232, 240, 0.8)',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === 'dark' ? '#8b5cf6' : '#6366f1',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === 'dark' ? '#8b5cf6' : '#6366f1',
                      borderWidth: '1px',
                    },
                    '&.Mui-focused': {
                      background: mode === 'dark' 
                        ? 'rgba(39, 39, 42, 0.6) !important' 
                        : 'rgba(255, 255, 255, 0.95) !important',
                      boxShadow: mode === 'dark'
                        ? '0 0 0 2px rgba(139, 92, 246, 0.1)'
                        : '0 0 0 2px rgba(99, 102, 241, 0.1)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 600,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiOutlinedInput-input': {
                    color: mode === 'dark' ? '#ffffff' : '#0f172a',
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 5, 
          pt: 3, 
          gap: 3,
          background: mode === 'dark'
            ? 'linear-gradient(135deg, rgba(39, 39, 42, 0.3) 0%, transparent 100%)'
            : 'linear-gradient(135deg, rgba(226, 232, 240, 0.3) 0%, transparent 100%)',
          borderTop: mode === 'dark' 
            ? '2px solid rgba(39, 39, 42, 0.6)' 
            : '2px solid rgba(226, 232, 240, 0.8)',
        }}>
          <Button 
            onClick={() => setCreateOpen(false)}
            variant="outlined"
            sx={{ 
              borderRadius: 4, 
              textTransform: 'none',
              fontWeight: 700,
              px: 5,
              py: 2,
              minHeight: 52,
              fontSize: '1rem',
              border: mode === 'dark' 
                ? '2px solid rgba(39, 39, 42, 0.8)' 
                : '2px solid rgba(226, 232, 240, 0.9)',
              color: mode === 'dark' ? '#ffffff' : '#0f172a',
              '&:hover': {
                borderColor: mode === 'dark' ? '#8b5cf6' : '#6366f1',
                backgroundColor: mode === 'dark' 
                  ? 'rgba(139, 92, 246, 0.1)' 
                  : 'rgba(99, 102, 241, 0.1)',
                transform: 'translateY(-2px)',
                boxShadow: mode === 'dark'
                  ? '0 8px 24px rgba(139, 92, 246, 0.2)'
                  : '0 8px 24px rgba(99, 102, 241, 0.2)',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleCreateTicket}
            disabled={!newTicket.summary.trim()}
            sx={{ 
              borderRadius: 4, 
              textTransform: 'none',
              fontWeight: 700,
              px: 5,
              py: 2,
              minHeight: 52,
              fontSize: '1rem',
              background: mode === 'dark'
                ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              boxShadow: mode === 'dark'
                ? '0 8px 32px rgba(139, 92, 246, 0.4)'
                : '0 8px 32px rgba(99, 102, 241, 0.3)',
              '&:hover': {
                background: mode === 'dark'
                  ? 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)'
                  : 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
                transform: 'translateY(-2px)',
                boxShadow: mode === 'dark'
                  ? '0 12px 40px rgba(139, 92, 246, 0.5)'
                  : '0 12px 40px rgba(99, 102, 241, 0.4)',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:disabled': {
                background: mode === 'dark' 
                  ? 'rgba(39, 39, 42, 0.6)' 
                  : 'rgba(226, 232, 240, 0.8)',
                color: mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.4)' 
                  : 'rgba(15, 23, 42, 0.4)',
              },
            }}
          >
            <AddIcon sx={{ mr: 1, fontSize: 20 }} />
            Create Ticket
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </Box>
  );
};

export default MyTicketsPage;
